import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  BadgeOutlined as BadgeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { NATIONALITY_OPTIONS } from '../../constants/nationalities';
import type { TruckSchema } from '../../types/transportation.type';
import { SetupCoachmark } from '../selfSetup/SetupCoachmark';

const GENDER_OPTIONS = ['Male', 'Female'] as const;

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '10px' },
} as const;

const formGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: 2,
  '& > .full-width': { gridColumn: '1 / -1' },
} as const;

function FormSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        fontWeight: 600,
        letterSpacing: 0,
        display: 'block',
        mb: 1.5,
      }}
    >
      {children}
    </Typography>
  );
}

export type DriverProfileFormState = {
  first_name: string;
  last_name: string;
  phone: string;
  gender: string;
  nationality: string;
  date_of_birth: string;
  iban: string;
  driver_license_expiry: string;
  driver_license_number: string;
  vehicle_registration_number: string;
};

export type DriverCreateFormState = DriverProfileFormState & {
  username: string;
  password: string;
  password_confirm: string;
  truck_id: string;
};

type DriverFormDialogBaseProps = {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  /** Whether the current user is permitted to submit this form (add or edit). */
  canSubmit: boolean;
  onSubmit: (event: React.FormEvent) => void;
  tourFormHint?: boolean;
  tourSubmitHint?: boolean;
  onTourFormNext?: () => void;
  onTourSubmitDone?: () => void;
  onTourDismiss?: () => void;
};

type DriverCreateDialogProps = DriverFormDialogBaseProps & {
  mode: 'create';
  formData: DriverCreateFormState;
  setFormData: React.Dispatch<React.SetStateAction<DriverCreateFormState>>;
  formErrors: Record<string, string>;
  availableTrucks: TruckSchema[];
};

type DriverEditDialogProps = DriverFormDialogBaseProps & {
  mode: 'edit';
  formData: DriverProfileFormState;
  setFormData: React.Dispatch<React.SetStateAction<DriverProfileFormState>>;
  formErrors: Record<string, string>;
  username: string;
};

export type DriverFormDialogProps = DriverCreateDialogProps | DriverEditDialogProps;

function ProfileFields({
  formData,
  formErrors,
  onChange,
}: {
  formData: DriverProfileFormState;
  formErrors: Record<string, string>;
  onChange: (patch: Partial<DriverProfileFormState>) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      <Box>
        <FormSectionTitle>{t('auth.personal')}</FormSectionTitle>
        <Box sx={formGridSx}>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('auth.firstName')}
            value={formData.first_name}
            onChange={(e) => onChange({ first_name: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('auth.lastName')}
            value={formData.last_name}
            onChange={(e) => onChange({ last_name: e.target.value })}
          />
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('common.phone')}
            value={formData.phone}
            onChange={(e) => onChange({ phone: e.target.value })}
            error={!!formErrors.phone}
            helperText={formErrors.phone || t('auth.phoneHint')}
          />
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            select
            label={t('auth.gender')}
            value={formData.gender}
            onChange={(e) => onChange({ gender: e.target.value })}
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {GENDER_OPTIONS.map((g) => (
              <MenuItem key={g} value={g}>
                {g === 'Male' ? t('auth.male') : t('auth.female')}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            select
            label={t('auth.nationality')}
            value={formData.nationality}
            onChange={(e) => onChange({ nationality: e.target.value })}
          >
            <MenuItem value="">{t('common.none')}</MenuItem>
            {NATIONALITY_OPTIONS.map((n) => (
              <MenuItem key={n.value} value={n.value}>
                {t(n.labelKey)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('auth.dateOfBirth')}
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => onChange({ date_of_birth: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            className="full-width"
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('auth.iban')}
            value={formData.iban}
            onChange={(e) => onChange({ iban: e.target.value })}
          />
        </Box>
      </Box>

      <Box>
        <FormSectionTitle>{t('transportation.drivers.driverAndVehicle')}</FormSectionTitle>
        <Box sx={formGridSx}>
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('transportation.drivers.driverLicenseNumber')}
            required
            value={formData.driver_license_number}
            onChange={(e) => onChange({ driver_license_number: e.target.value })}
            error={!!formErrors.driver_license_number}
            helperText={formErrors.driver_license_number}
          />
          <TextField
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('transportation.drivers.driverLicenseExpiry')}
            type="date"
            value={formData.driver_license_expiry}
            onChange={(e) => onChange({ driver_license_expiry: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            className="full-width"
            fullWidth
            size="small"
            sx={fieldSx}
            label={t('transportation.drivers.vehicleRegistrationNumber')}
            required
            value={formData.vehicle_registration_number}
            onChange={(e) => onChange({ vehicle_registration_number: e.target.value })}
            error={!!formErrors.vehicle_registration_number}
            helperText={formErrors.vehicle_registration_number}
          />
        </Box>
      </Box>
    </>
  );
}

const DriverFormDialog: React.FC<DriverFormDialogProps> = (props) => {
  const {
    open,
    onClose,
    loading,
    canSubmit,
    onSubmit,
    mode,
    tourFormHint = false,
    tourSubmitHint = false,
    onTourFormNext,
    onTourSubmitDone,
    onTourDismiss,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isEdit = mode === 'edit';
  const showCreateTour = tourFormHint && open && mode === 'create';
  const showSubmitTour = tourSubmitHint && open && mode === 'create';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 2.5,
          py: 2,
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              flexShrink: 0,
            }}
          >
            <BadgeIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, m: 0 }}>
              {isEdit
                ? t('transportation.drivers.editDialogTitle')
                : t('transportation.drivers.addDialogTitle')}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.25 }}
            >
              {isEdit ? t('common.edit') : t('common.add')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <Box component="form" onSubmit={onSubmit} noValidate>
        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <SetupCoachmark
            open={showCreateTour}
            fullWidth
            title={t('selfSetup.tour.driversFormTitle')}
            subtitle={t('selfSetup.tour.driversFormSubtitle')}
            nextLabel={t('selfSetup.tourNext')}
            onNext={() => onTourFormNext?.()}
            onDismiss={() => onTourDismiss?.()}
          >
            <Stack spacing={3} divider={<Divider flexItem sx={{ opacity: 0.55 }} />}>
              <Box>
                <FormSectionTitle>{t('auth.account')}</FormSectionTitle>
              <Box sx={formGridSx}>
                {mode === 'create' ? (
                  <>
                    <TextField
                      className="full-width"
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      label={t('transportation.drivers.usernameEmail')}
                      type="email"
                      value={props.formData.username}
                      onChange={(e) =>
                        props.setFormData({ ...props.formData, username: e.target.value })
                      }
                      error={!!props.formErrors.username}
                      helperText={props.formErrors.username}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      label={t('auth.password')}
                      type="password"
                      required
                      value={props.formData.password}
                      onChange={(e) =>
                        props.setFormData({ ...props.formData, password: e.target.value })
                      }
                      error={!!props.formErrors.password}
                      helperText={props.formErrors.password}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      sx={fieldSx}
                      label={t('auth.confirmPassword')}
                      type="password"
                      required
                      value={props.formData.password_confirm}
                      onChange={(e) =>
                        props.setFormData({
                          ...props.formData,
                          password_confirm: e.target.value,
                        })
                      }
                      error={!!props.formErrors.password_confirm}
                      helperText={props.formErrors.password_confirm}
                    />
                  </>
                ) : (
                  <TextField
                    className="full-width"
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    label={t('transportation.drivers.usernameEmail')}
                    type="email"
                    disabled
                    value={props.username}
                    helperText={t('transportation.drivers.usernameCannotChange')}
                  />
                )}
              </Box>
            </Box>

            <Box>
              {mode === 'create' ? (
                <ProfileFields
                  formData={props.formData}
                  formErrors={props.formErrors}
                  onChange={(patch) =>
                    props.setFormData((prev: DriverCreateFormState) => ({ ...prev, ...patch }))
                  }
                />
              ) : (
                <ProfileFields
                  formData={props.formData}
                  formErrors={props.formErrors}
                  onChange={(patch) =>
                    props.setFormData((prev: DriverProfileFormState) => ({ ...prev, ...patch }))
                  }
                />
              )}
            </Box>

            {mode === 'create' ? (
              <Box>
                <FormSectionTitle>{t('transportation.drivers.assignedTruck')}</FormSectionTitle>
                <Box sx={formGridSx}>
                  <TextField
                    className="full-width"
                    fullWidth
                    size="small"
                    sx={fieldSx}
                    select
                    label={t('transportation.drivers.assignToTruckOptional')}
                    value={props.formData.truck_id}
                    onChange={(e) =>
                      props.setFormData({ ...props.formData, truck_id: e.target.value })
                    }
                  >
                    <MenuItem value="">{t('common.none')}</MenuItem>
                    {props.availableTrucks.map((truck) => (
                      <MenuItem key={truck.id} value={truck.id}>
                        {truck.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            ) : null}
            </Stack>
          </SetupCoachmark>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            {t('common.cancel')}
          </Button>
          <SetupCoachmark
            open={showSubmitTour}
            title={t('selfSetup.tour.driversSubmitTitle')}
            subtitle={t('selfSetup.tour.driversSubmitSubtitle')}
            nextLabel={t('selfSetup.tourDone')}
            onNext={() => onTourSubmitDone?.()}
            onDismiss={() => onTourDismiss?.()}
          >
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !canSubmit}
              startIcon={isEdit ? <SaveIcon /> : <AddIcon />}
              sx={{ textTransform: 'none' }}
            >
              {loading
                ? isEdit
                  ? t('transportation.drivers.updating')
                  : t('transportation.drivers.creating')
                : isEdit
                  ? t('transportation.drivers.updateDriver')
                  : t('transportation.drivers.createDriver')}
            </Button>
          </SetupCoachmark>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default DriverFormDialog;
